import React from 'react';
import { ExternalLink } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { MultiSelect } from "@/components/ui/multi-select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Badge } from "@/components/ui/badge";
import { StyledBadge } from '../StyledBadge';
import { CreatorInfo } from '../CreatorInfo';
import { cn } from "@/lib/utils";
import { KolDataWithDates } from '@/types/table';

interface Option {
  value: string;
  label: string;
}

interface ColumnConfig {
  renderEditableTextCell: (row: KolDataWithDates, field: keyof KolDataWithDates, placeholder?: string) => React.ReactNode;
  renderEditableNumberCell: (row: KolDataWithDates, field: keyof KolDataWithDates, placeholder?: string) => React.ReactNode;
  renderEditableUrlCell: (row: KolDataWithDates, field: keyof KolDataWithDates, placeholder?: string) => React.ReactNode;
  renderEditableCell: (row: KolDataWithDates, field: keyof KolDataWithDates, options: string[], badgeVariant?: string, placeholder?: string) => React.ReactNode;
  renderTextCell: (text: string | null | undefined, maxLength?: number) => React.ReactNode;
  renderMultiSelectCampaignBuCell: (row: KolDataWithDates) => React.ReactNode;
  renderCampaignBuCell: (row: KolDataWithDates) => React.ReactNode;
  renderBusinessUnitCell: (row: KolDataWithDates) => React.ReactNode;
  renderContentIdCell: (row: KolDataWithDates) => React.ReactNode;
  renderEditableDateCell: (row: KolDataWithDates, field: keyof KolDataWithDates) => React.ReactNode;
  handleToggleIntegration: (rowData: KolDataWithDates, checked: boolean) => void;
  handlePicChange: (row: KolDataWithDates, selectedPics: string[]) => void;
  getContentTypeOptionsForKol: (kolDbId: string | undefined) => string[];
  getKolDbInfo: (kolDbId: string | undefined) => any;
  getTypeVariant: (type: string) => "blue" | "red" | "purple" | "green" | "yellow";
  picOptions: Option[];
  hasFullAccess: boolean;
  updateRow: (rowId: string, field: keyof KolDataWithDates, value: any) => Promise<void>;
  PLATFORM_OPTIONS: string[];
  POST_TYPE_OPTIONS: string[];
  TIER_OPTIONS: string[];
  LANGUAGE_OPTIONS: string[];
  CONTENT_TYPE_OPTIONS: string[];
  CONTENT_ID_OPTIONS?: Array<{
    value: string;
    label: string;
    description?: string;
    tags?: string[];
    pic?: string[];
  }>;
  isEditable?: boolean; // Add this prop to control editability
  hasBuAccess?: boolean; // Add this prop to check BU Access
}

export const createKolPostingLinksColumns = (config: ColumnConfig) => {
  const {
    renderEditableTextCell,
    renderEditableNumberCell,
    renderEditableUrlCell,
    renderEditableCell,
    renderTextCell,
    renderMultiSelectCampaignBuCell,
    renderCampaignBuCell,
    renderBusinessUnitCell,
    renderEditableDateCell,
    handleToggleIntegration,
    handlePicChange,
    getContentTypeOptionsForKol,
    getKolDbInfo,
    getTypeVariant,
    picOptions,
    hasFullAccess,
    updateRow,
    PLATFORM_OPTIONS,
    POST_TYPE_OPTIONS,
    TIER_OPTIONS,
    LANGUAGE_OPTIONS,
    CONTENT_TYPE_OPTIONS,
    CONTENT_ID_OPTIONS = [],
    isEditable = true,
    hasBuAccess = false
  } = config;

  // Define the filter function
  const filterByCampaign = (row: any, columnId: string, filterValue: any) => {
    if (!filterValue || filterValue.length === 0) return true;
    const rowValue = row.getValue(columnId);
    return filterValue.includes(rowValue);
  };

  // Define platforms that support integration
  const INTEGRATION_SUPPORTED_PLATFORMS = ['twitter', 'youtube', 'telegram'];

  return [
    { 
      id: 'name', 
      header: 'Content Creator',
      accessorKey: 'name' as keyof KolDataWithDates,
      minWidth: 100,
      sortable: true,
      editable: false,
      cell: (row: KolDataWithDates) => {
        const kolInfo = getKolDbInfo(row.kol_db_id);
        
        // CRITICAL: Direct check for BU access and name editability
        const shouldAllowNameEdit = !hasBuAccess;
        const nameOnChangeHandler = shouldAllowNameEdit ? (newName: string) => {
          console.log('✅ Name change allowed for:', row.name, 'to:', newName);
          updateRow(row.id, 'name', newName);
        } : undefined;
        
        return (
          <div className="px-2 py-1 max-w-full overflow-hidden">
            <CreatorInfo 
              name={row.name} 
              handler={row.channel} 
              platform={kolInfo?.social_media_type || row.platform} 
              region={kolInfo?.region || null}
              profileImageUrl={kolInfo?.profile_image_url || null}
              editable={shouldAllowNameEdit} // Direct boolean value
              onNameChange={nameOnChangeHandler} // Undefined for BU access users
            />
          </div>
        );
      }
    },
    { 
      id: 'tier', 
      header: 'Tier',
      accessorKey: 'tier' as keyof KolDataWithDates,
      minWidth: 100,
      sortable: true,
      editable: isEditable,
      inputType: "select" as const,
      selectOptions: TIER_OPTIONS,
      cell: (row: KolDataWithDates) => {
        return renderEditableCell(row, 'tier', TIER_OPTIONS, 'dark', 'Select tier');
      }
    },
    { 
      id: 'language', 
      header: 'Language',
      accessorKey: 'language',
      enableColumnFilter: true,
      filterFn: 'equals',
      enableSorting: true,
      minWidth: 100,
      editable: true,
      sortable: true,
      filterSelectOptions: LANGUAGE_OPTIONS,
      cell: (row: KolDataWithDates) => {
        return renderEditableCell(row, 'language', LANGUAGE_OPTIONS, 'purple', 'Select language');
      }
    },
    { 
      id: 'type', 
      header: 'Type',
      accessorKey: 'type',
      minWidth: 195,
      width: 195,
      enableColumnFilter: true,
      filterFn: (row: any, columnId: string, filterValue: any) => {
        if (!filterValue || filterValue.length === 0) return true;
        const rowValue = row.getValue(columnId);
        return filterValue.includes(rowValue);
      },
      filterSelectOptions: CONTENT_TYPE_OPTIONS,
      inputType: "select" as const,
      selectOptions: CONTENT_TYPE_OPTIONS,
      editable: isEditable,
      sortable: true,
      cell: (row: KolDataWithDates) => {
        const variant = getTypeVariant(row.type || '');
        return renderEditableCell(row, 'type', CONTENT_TYPE_OPTIONS, variant, 'Select type');
      }
    },
    { 
      id: 'channel', 
      header: 'Channel',
      accessorKey: 'channel' as keyof KolDataWithDates,
      minWidth: 120,
      width: 120,
      sortable: true,
      editable: isEditable && !hasBuAccess,
      cell: (row: KolDataWithDates) => (
        <div className="px-2 py-1 max-w-full overflow-hidden">
          {renderEditableTextCell(row, 'channel', 'Enter channel name')}
        </div>
      )
    },
    { 
      id: 'platform', 
      header: 'Platform',
      accessorKey: 'platform' as keyof KolDataWithDates,
      minWidth: 130,
      sortable: true,
      editable: true,
      inputType: "select" as const,
      selectOptions: PLATFORM_OPTIONS,
      cell: (row: KolDataWithDates) => {
        return renderEditableCell(row, 'platform', PLATFORM_OPTIONS, 'blue', 'Select platform');
      }
    },
    { 
      id: 'post_type', 
      header: 'KOL Type',
      accessorKey: 'post_type' as keyof KolDataWithDates,
      minWidth: 150,
      width: 150,
      sortable: true,
      editable: true,
      inputType: "select" as const,
      selectOptions: POST_TYPE_OPTIONS,
      cell: (row: KolDataWithDates) => {
        return renderEditableCell(row, 'post_type', POST_TYPE_OPTIONS, 'green', 'Select KOL type');
      }
    },
    {
      id: 'post_link',
      header: 'Post Link',
      accessorKey: 'post_link' as keyof KolDataWithDates,
      minWidth: 200,
      width: 300,
      sortable: true,
      editable: true,
      cell: (row: KolDataWithDates) => (
        <div className="px-2 py-1 max-w-full overflow-hidden">
          {renderEditableUrlCell(row, 'post_link', 'Enter post URL')}
        </div>
      )
    },
    { 
      id: 'content_views', 
      header: 'Content Views',
      accessorKey: 'content_views' as keyof KolDataWithDates,
      minWidth: 120,
      sortable: true,
      editable: true,
      cell: (row: KolDataWithDates) => {
        console.log('🔍 Content Views Column - Row data:', { id: row.id, content_views: row.content_views });
        return (
          <div className="px-2 py-1 max-w-full overflow-hidden">
            {renderEditableNumberCell(row, 'content_views', 'Enter views')}
          </div>
        );
      }
    },
    { 
      id: 'expense', 
      header: 'Expense',
      accessorKey: 'expense' as keyof KolDataWithDates,
      minWidth: 200,
      width: 200,
      sortable: true,
      editable: true,
      cell: (row: KolDataWithDates) => {
        console.log('🔍 Expense Column - Row data:', { id: row.id, expense: row.expense });
        return (
          <div className="px-2 py-1 max-w-full overflow-hidden">
            {renderEditableNumberCell(row, 'expense', 'Enter expense')}
          </div>
        );
      }
     },
    { 
      id: 'notes', 
      header: 'Notes',
      accessorKey: 'notes' as keyof KolDataWithDates,
      minWidth: 200,
      width: 200,
      sortable: true,
      editable: true,
      cell: (row: KolDataWithDates) => (
        <div className="px-2 py-1 max-w-full overflow-hidden">
          {renderEditableTextCell(row, 'notes', 'Add notes...')}
        </div>
      )
    },
    { 
      id: 'campaign_bu', 
      header: 'Content ID',
      accessorKey: 'campaign_bu' as keyof KolDataWithDates,
      minWidth: 200,
      width: 200,
      sortable: true,
      editable: isEditable && !hasBuAccess,
      inputType: "select" as const,
      cell: (row: KolDataWithDates) => {
        const currentValue = Array.isArray(row.campaign_bu) ? row.campaign_bu[0] || '' : row.campaign_bu || '';
        
        // Find the selected content ID option to get its tags
        const selectedOption = CONTENT_ID_OPTIONS.find(option => option.value === currentValue);
        const tags = selectedOption?.tags || [];
        
        const handleChange = async (value: string) => {
          if (!isEditable || hasBuAccess) return; // Prevent changes for BU Access users
          console.log(`Content ID: Changing to:`, value);
          await updateRow(row.id, 'campaign_bu', value ? [value] : []);
        };
        
        return (
          <div className="px-2 py-1 max-w-full overflow-hidden">
            <div className="space-y-1">
              <SearchableSelect
                options={CONTENT_ID_OPTIONS.map(option => option.value)}
                value={currentValue}
                onChange={handleChange}
                placeholder="Select Content ID"
                badgeVariant="blue"
                className="w-full max-w-full"
                clearable={true}
                disabled={!isEditable || hasBuAccess} // Disable for BU Access users
              />
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {tags.slice(0, 2).map((tag, index) => (
                    <Badge 
                      key={index} 
                      variant="outline" 
                      className="text-[9px] px-1 py-0 h-3.5 bg-gray-50 text-gray-600 border-gray-200 leading-none"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {tags.length > 2 && (
                    <Badge 
                      variant="outline" 
                      className="text-[9px] px-1 py-0 h-3.5 bg-gray-100 text-gray-500 border-gray-200 leading-none"
                    >
                      +{tags.length - 2}
                    </Badge>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }
    },
    { 
      id: 'business_unit', 
      header: 'Business Unit',
      accessorKey: 'business_unit' as keyof KolDataWithDates,
      minWidth: 250,
      width: 250,
      sortable: true,
      editable: true,
      inputType: "select" as const,
      cell: renderBusinessUnitCell,
      hidden: true
    },
    { 
      id: 'pic', 
      header: 'PIC',
      accessorKey: 'pic' as keyof KolDataWithDates,
      minWidth: 252,
      width: 252,
      sortable: true,
      editable: hasFullAccess,
      cell: (row: KolDataWithDates) => {
        const picArray = Array.isArray(row.pic) ? row.pic : [];
        
        return (
          <div className="px-2 py-1 max-w-full overflow-hidden">
            {!hasFullAccess ? (
              <div className="flex flex-wrap gap-1">
                {picArray.length > 0 ? picArray.map((pic, index) => (
                  <StyledBadge key={index} value={pic} extraSmall />
                )) : (
                  <span className="text-[#8E9196] text-xs">No PIC assigned</span>
                )}
              </div>
            ) : (
              <MultiSelect
                options={picOptions}
                selected={picArray}
                onChange={(selected) => handlePicChange(row, selected)}
                placeholder="Select PICs"
                maxDisplay={2}
                className="w-full"
              />
            )}
          </div>
        );
      }
    },
    { 
      id: 'posting_date', 
      header: 'Posting Date',
      accessorKey: 'posting_date' as keyof KolDataWithDates,
      minWidth: 120,
      sortable: true,
      editable: isEditable && !hasBuAccess,
      cell: (row: KolDataWithDates) => (
        <div className="px-2 py-1 max-w-full overflow-hidden">
          {renderEditableDateCell(row, 'posting_date')}
        </div>
      )
    },
    { 
      id: 'integration_enable', 
      header: 'Integration Enable',
      accessorKey: 'integration_enable' as keyof KolDataWithDates,
      minWidth: 80,
      width: 80,
      sortable: true,
      editable: isEditable,
      hidden: hasBuAccess,
      cell: (row: KolDataWithDates) => {
        const platform = (row.platform || '').toLowerCase();
        const isIntegrationSupported = ['twitter', 'youtube', 'telegram'].includes(platform);
        
        return (
          <div className="px-2 py-1 max-w-full overflow-hidden">
            {isIntegrationSupported ? (
              <Switch 
                checked={row.integration_enable} 
                onCheckedChange={(checked) => {
                  if (isEditable) {
                    handleToggleIntegration(row, checked);
                  }
                }}
                className="data-[state=checked]:bg-primary"
                disabled={!isEditable}
              />
            ) : (
              <span className="text-muted-foreground text-xs">-</span>
            )}
          </div>
        );
      }
    }
  ];
};
